import { HandlerEvent } from 'post-robot';
import { pushErrors, pushIsRecognizedRequest, pushIsRecognizedResponse } from 'common/gamp';
import { saveUser, UserProfile } from 'communicator/store';
import {
  getApiKey,
  getDcfExperience,
  getIsFeatureToggleEnabled,
  getIsVdiDisabled,
  getIsVisaSRCi,
  getMeta,
  getSessionId
} from 'communicator/store/selectors';
import { fetchOrchestratorRecognize } from 'communicator/utils/api/recognized';
import {
  getUsernameFromErmCookie,
  getUsernameFromSsiCookie,
  setErmEnabled
} from 'communicator/utils/cookies';
import { getOrchestratorLockoutFlow } from 'communicator/utils/get-lockout-flow';
import { markEnd, markStart } from 'communicator/utils/instrumentation';
import parseExpiryFromAccessToken from 'communicator/utils/parse-expiry-from-token';
import { localStorage, STARTING_TAB } from 'communicator/utils/storage';
import { FetchRecognizedOptions } from 'types/api/reissuance';
import { IsRecognizedPayload, IsRecognizedRequestPayload } from 'types/visa-sdk/is-recognized';

export default async function isRecognized(
  event?: HandlerEvent<IsRecognizedRequestPayload>
): Promise<IsRecognizedPayload> {
  try {
    markStart('IS_RECOGNIZED');
    markStart('IS_RECOGNIZED_LEGACY');
    markStart('IS_RECOGNIZED_NO_COOKIE');
    markStart('IS_RECOGNIZED_NO_COOKIE_LEGACY');
    const stime = Date.now();
    const apikey = getApiKey();
    const sessionId = getSessionId();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const { data } = event ?? {};
    /* START GENAI@GHCOPILOT */
    const id_token = data?.idTokens?.[0] ?? null;

    const recognition_token = data?.recognitionTokens?.[0] ?? null;

    const recognizedResponseOptions: FetchRecognizedOptions = {
      apikey,
      idToken: id_token ?? undefined,
      recognitionToken: recognition_token ?? undefined,
      sessionId
    };

    const isIntegrated = getDcfExperience() === 'integrated';

    if (getIsFeatureToggleEnabled('fidoAuthEnabled') && isIntegrated) {
      recognizedResponseOptions.isBiometricsEnabledDevice = getMeta().isBiometricsEnabledDevice;
    }

    const isVdiDisabled = getIsVdiDisabled();

    if (getIsFeatureToggleEnabled('enableVpps') && isIntegrated && isVdiDisabled) {
      recognizedResponseOptions.isVdiDisabled = isVdiDisabled;
    }
    /* END GENAI@GHCOPILOT */

    pushIsRecognizedRequest();

    const recognizedResponse = await fetchOrchestratorRecognize(recognizedResponseOptions);
    const isVisaSrci = getIsVisaSRCi();
    const usedDevice = Boolean(localStorage.getItem(STARTING_TAB));

    if (!recognizedResponse.ok || !recognizedResponse.data) {
      pushIsRecognizedResponse('Unrecognized', Date.now() - stime);

      const visaSrciOnlyPayload = isVisaSrci ? { usedDevice } : null;

      const unrecognizedPayload: IsRecognizedPayload = {
        ...visaSrciOnlyPayload,
        recognized: false
      };

      if (!recognizedResponse.ok && getOrchestratorLockoutFlow(recognizedResponse)) {
        saveUser({
          isLocked: true
        });

        unrecognizedPayload.error = { reason: 'ACCT_INACCESSIBLE' };
        markEnd('IS_RECOGNIZED', { doFlush: true, failure: true, reason: 'ACCT_INACCESSIBLE' });
        // START GENAI@GHCOPILOT
        pushErrors('IS_RECOGNIZED', 'ACCT_INACCESSIBLE');
        // END GENAI@GHCOPILOT
      } else if (!recognizedResponse.ok) {
        markEnd('IS_RECOGNIZED', { doFlush: true, failure: true, reason: 'UNKNOWN_FAILURE' });
        // START GENAI@GHCOPILOT
        pushErrors('IS_RECOGNIZED', 'UNKNOWN_FAILURE');
        // END GENAI@GHCOPILOT
      } else {
        markEnd('IS_RECOGNIZED_NO_COOKIE', { doFlush: true });
      }

      return unrecognizedPayload;
    }

    pushIsRecognizedResponse('Recognized', Date.now() - stime);

    const expiresIn = parseExpiryFromAccessToken(
      recognizedResponse.data.recognitionToken
    ) as number;

    const accessToken =
      recognizedResponse.headers.access_token || recognizedResponse.data.recognitionToken;

    const userProfileUpdates: UserProfile = {
      accessToken: {
        expiresIn,
        isPartial: false,
        token: accessToken
      },
      country: recognizedResponse.data.customRecognitionData.locality.country,
      email: getUsernameFromSsiCookie() || getUsernameFromErmCookie(),
      flowType: 'standard',
      isRecognizedUser: true,
      locale: recognizedResponse.data.customRecognitionData.locality.locale
    };

    const { tncEntryInfo } = recognizedResponse.data.customRecognitionData;

    if (tncEntryInfo) {
      userProfileUpdates.tnc = {
        status: 'not_accepted',
        tncEntryInfo
      };
    }

    if (recognizedResponse.data.customRecognitionData.tokenType === 'SSI') {
      userProfileUpdates.flowType = 'ssi';
    } else {
      userProfileUpdates.flowType = 'erm';
      setErmEnabled();
    }

    const idToken = recognizedResponse.data.idTokens[0];

    if (idToken) {
      userProfileUpdates.idToken = idToken;
    }

    saveUser(userProfileUpdates);

    markEnd('IS_RECOGNIZED', { doFlush: true });

    const visaSrciOnlyPayload = isVisaSrci
      ? {
          customData: {
            auth_status: 'FULL' as const,
            creation_timestamp: Date.now(),
            data: {
              locality: recognizedResponse.data.customRecognitionData.locality
            },
            expires_in: expiresIn
          },
          usedDevice
        }
      : null;

    return {
      ...visaSrciOnlyPayload,
      idTokens: idToken ? [idToken] : [],
      recognized: true
    };
  } catch (err: unknown) {
    // START GENAI@GHCOPILOT
    pushErrors('IS_RECOGNIZED', err);

    return {
      recognized: false
    };
    // END GENAI@GHCOPILOT
  }
}
